/**
 * scene.ts — Renderer, scene, lighting, and resize handling.
 *
 * Sets up the WebGL context and the light rig that gives the top-down city its
 * depth: a soft ambient fill plus a directional "sun" that casts the shadows.
 * The shadow camera is a fixed-size frustum that follows the walker — see
 * `SHADOW_RADIUS` for why it is not sized to the level any more.
 */
import * as THREE from 'three';
import { CAMERA, WORLD, LIGHTING } from '../config/constants';
import { activeLevel } from '../config/level';

export interface Renderable {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  dispose(): void;
}

/**
 * The slice of `THREE.WebGLRenderer` the game actually uses.
 *
 * `THREE.WebGLRenderer` satisfies this structurally, so production code is
 * unchanged — but naming the surface means `Game` can be handed a stand-in and
 * tested without a GPU. Creating a real renderer is the *only* part of the game
 * core that needs one; the scene, the world geometry, and every entity rig build
 * fine without it.
 */
export interface GameRenderer {
  setAnimationLoop(callback: (() => void) | null): void;
  render(scene: THREE.Scene, camera: THREE.Camera): void;
  setPixelRatio(value: number): void;
  setSize(width: number, height: number): void;
  dispose(): void;
  readonly domElement: HTMLCanvasElement;
}

export function createRenderer(canvasParent: HTMLElement): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  // Cap the pixel ratio: full DPR on a phone is a big fill-rate cost for little gain.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  // PCF soft, NOT VSM. VSM is the only type that honours `shadow.radius`, so it
  // looks like the obvious pick for wide clay-soft penumbrae — but across a
  // shadow frustum this large it lays fine diagonal acne streaks over every flat
  // surface on the map. The softness is bought with the light rig instead (a
  // strong hemisphere fill against a gentle sun, see LIGHTING).
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  // No tone mapping. Clay wants flat, saturated, poster-ish colour; every
  // filmic curve tried here (ACES, Neutral) desaturated the palette and greyed
  // out the warm stone the town is built from. Banding is handled per-material
  // by `dithering` instead — see render/clay.ts.
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = LIGHTING.EXPOSURE;
  canvasParent.appendChild(renderer.domElement);
  return renderer;
}

export function createScene(): THREE.Scene {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(WORLD.BACKGROUND_COLOR);
  // Fog hides the level edges so the city reads as part of something bigger.
  scene.fog = new THREE.Fog(WORLD.BACKGROUND_COLOR, WORLD.FOG_NEAR, WORLD.FOG_FAR);

  // Sky-to-ground fill: warmer and more natural outdoors than a flat ambient.
  scene.add(
    new THREE.HemisphereLight(
      LIGHTING.SKY_COLOR,
      LIGHTING.GROUND_BOUNCE_COLOR,
      LIGHTING.HEMI_INTENSITY,
    ),
  );

  const bounds = activeLevel().bounds;
  const midX = bounds.x + bounds.w / 2;
  const midZ = bounds.y + bounds.h / 2;
  const sun = new THREE.DirectionalLight(LIGHTING.SUN_COLOR, LIGHTING.SUN_INTENSITY);
  // Offset is relative to whatever the sun is aimed at, so the angle is the same
  // wherever on the map that happens to be.
  sun.position.set(
    midX + LIGHTING.SUN_OFFSET.x,
    LIGHTING.SUN_OFFSET.y,
    midZ + LIGHTING.SUN_OFFSET.z,
  );
  sun.castShadow = true;
  sun.shadow.mapSize.set(LIGHTING.SHADOW_MAP_SIZE, LIGHTING.SHADOW_MAP_SIZE);
  // Rounded clay has grazing-angle surfaces all over it, which is exactly where
  // shadow acne appears. normalBias offsets the lookup along the surface normal
  // and clears it without the peter-panning a flat depth bias would cause.
  sun.shadow.normalBias = LIGHTING.SHADOW_NORMAL_BIAS;

  const cam = sun.shadow.camera as THREE.OrthographicCamera;
  cam.left = -SHADOW_RADIUS;
  cam.right = SHADOW_RADIUS;
  cam.top = SHADOW_RADIUS;
  cam.bottom = -SHADOW_RADIUS;
  cam.near = 1;
  // Far enough to reach the ground from the sun's own height, plus the diagonal
  // of the frustum it is looking through — it no longer scales with the map, so
  // this can be worked out once rather than guessed at.
  cam.far = LIGHTING.SUN_OFFSET.y + SHADOW_RADIUS * 2;
  cam.updateProjectionMatrix();

  sun.target.position.set(midX, 0, midZ);
  scene.add(sun.target);
  scene.add(sun);

  return scene;
}

/**
 * Half-width of the sun's shadow frustum, in world units.
 *
 * **Fixed, and deliberately not sized to the level.** The frustum used to cover
 * the whole map, which was fine when a map was 960 × 640 and quietly stopped
 * being fine as towns grew: the shadow map is `SHADOW_MAP_SIZE` texels however
 * big the frustum is, so doubling the map halves the shadow resolution
 * everywhere. On a town with a surround round it — several thousand units
 * across — the same rig was spending most of its texels on ground beyond the
 * fog and drawing the town itself at about two units per texel.
 *
 * So the frustum is sized to what the camera can actually see and follows the
 * walker instead (`updateShadowFocus`). Shadow quality then has nothing to do
 * with map size, which is what makes every later size increase free.
 *
 * The radius covers the visible ground with room to spare: the camera sits
 * `CAMERA.DISTANCE` back, and a caster has to be in the frustum before it comes
 * on screen or its shadow pops in at the edge of the view.
 */
const SHADOW_RADIUS = CAMERA.DISTANCE + 240;

/**
 * Move the sun's shadow frustum to follow what the camera is looking at.
 *
 * Called once a frame with the camera's focus point. The **snap** is the part
 * that matters and the part that is easy to leave out: an orthographic shadow
 * frustum sliding continuously re-rasterises every shadow slightly differently
 * each frame, and the whole town crawls with a fine shimmer along every shadow
 * edge. Rounding the focus to whole shadow-map texels means the depth map
 * always samples the same world grid, so shadows sit still while the frustum
 * moves under them in discrete steps.
 */
export function updateShadowFocus(sun: THREE.DirectionalLight, x: number, z: number): void {
  const texel = (SHADOW_RADIUS * 2) / LIGHTING.SHADOW_MAP_SIZE;
  const sx = Math.round(x / texel) * texel;
  const sz = Math.round(z / texel) * texel;
  sun.target.position.set(sx, 0, sz);
  sun.target.updateMatrixWorld();
  sun.position.set(
    sx + LIGHTING.SUN_OFFSET.x,
    LIGHTING.SUN_OFFSET.y,
    sz + LIGHTING.SUN_OFFSET.z,
  );
}

/** The sun in a scene built by `createScene`, for the per-frame focus update. */
export function findSun(scene: THREE.Scene): THREE.DirectionalLight | undefined {
  return scene.children.find((c): c is THREE.DirectionalLight => c instanceof THREE.DirectionalLight);
}

/** Keep the canvas and camera matched to the viewport (and to device rotation). */
export function attachResize(
  renderer: GameRenderer,
  camera: THREE.PerspectiveCamera,
): () => void {
  const onResize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
  };
  window.addEventListener('resize', onResize);
  onResize();
  return () => window.removeEventListener('resize', onResize);
}
